import { calculateOrder, submitOrder, unitPrice } from '@caplahoma/order-core';

class QuickOrder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.forms = { single: this.querySelector('[data-single-form]'), bulk: this.querySelector('[data-bulk-form]') };
    this.status = this.querySelector('[data-order-status]');
    this.inputs = [...this.querySelectorAll('[data-quantity]')];
    this.buttons = [...this.querySelectorAll('[data-add-all], [data-add-single], [data-test-one]')];
    this.reviewLink = this.querySelector('[data-review-cart]');
    this.color = this.querySelector('[data-single-color]');
    this.quantity = this.querySelector('[data-single-quantity]');
    try { this.rules = JSON.parse(this.querySelector('[data-variant-rules]').textContent); } catch { return; }
    this.format = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
    this.querySelector('[data-native-order]').hidden = true;
    this.querySelector('[data-purchase-modes]').hidden = false;
    this.inputs.forEach(input => input.addEventListener('input', () => this.update()));
    this.quantity.addEventListener('input', () => this.update());
    this.querySelectorAll('[data-color-choice]').forEach(button => button.addEventListener('click', () => { this.color.value = button.dataset.colorChoice; this.refreshVariant(); this.update(); }));
    this.color.addEventListener('change', () => { this.refreshVariant(); this.update(); });
    this.querySelectorAll('[data-mode]').forEach(button => button.addEventListener('click', () => this.setMode(button.dataset.mode)));
    Object.values(this.forms).forEach(form => form.addEventListener('submit', event => this.submit(event)));
    this.querySelector('[data-test-one]').addEventListener('click', event => this.submit(event, true));
    this.refreshVariant();
    this.setMode('single');
  }
  money(value) { return this.format.format(value / 100); }
  selectedRule() { return this.rules.find(rule => String(rule.id) === this.color.value); }
  setMode(mode) {
    if (this.busy) return;
    this.mode = mode;
    for (const [key, form] of Object.entries(this.forms)) form.hidden = key !== mode;
    this.querySelectorAll('[data-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mode === mode)));
    this.update();
  }
  refreshVariant() {
    this.querySelectorAll('[data-color-choice]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.colorChoice === this.color.value)));
    const stock = this.querySelector(`[data-stock-for="${this.color.value}"]`);
    this.querySelector('[data-single-stock]').replaceChildren(stock.content.cloneNode(true));
    const media = this.closest('.pdp-grid')?.querySelector('[data-primary-media]');
    if (media) media.replaceChildren(this.querySelector(`[data-media-for="${this.color.value}"]`).content.cloneNode(true));
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.color.value);
    window.history.replaceState(null, '', url);
    const rule = this.selectedRule();
    const steps = [...new Set([1, ...rule.breaks.map(tier => tier.minimum)])].sort((a, b) => a - b);
    const body = this.querySelector('[data-price-ladder]');
    body.replaceChildren();
    for (const count of steps) {
      const row = document.createElement('tr');
      const label = document.createElement('th'); label.scope = 'row'; label.textContent = `${count}${count === steps.at(-1) && count > 1 ? '+' : ''}${count === 24 ? ' · 1 sleeve' : ''}`;
      if (count === 24) row.className = 'sleeve-tier';
      const value = document.createElement('td');
      value.textContent = count < rule.minimum || count % rule.increment !== 0 || (rule.maximum != null && count > rule.maximum) ? 'Outside quantity rules' : this.money(unitPrice(rule, count));
      row.append(label, value); body.append(row);
    }
    this.querySelector('[data-tier-note]').textContent = rule.breaks.length
      ? (rule.scope === 'product' ? 'Colors within this style combine for quantity pricing. Different styles qualify separately. Your cart confirms the final price.' : 'Quantity breaks apply to this option separately. Your cart confirms the final price.')
      : 'This color currently has one published unit price. Additional quantity discounts are not yet configured.';
  }
  getOrder(testOne = false) {
    const entries = this.mode === 'single' || testOne
      ? [{ id: this.color.value, quantity: testOne ? 1 : this.quantity.value, title: this.color.selectedOptions[0].textContent }]
      : this.inputs.map(input => ({ id: input.dataset.variantId, quantity: input.value, title: input.dataset.title }));
    return calculateOrder(this.rules, entries, { existingProductQuantity: Number(this.dataset.existingProductQuantity || 0) });
  }
  update() {
    if (this.busy || this.uncertain) return;
    this.buttons.forEach(button => { button.disabled = false; });
    const rule = this.selectedRule();
    this.querySelector('[data-test-one]').disabled = !rule.available || rule.minimum > 1 || rule.increment > 1;
    try {
      if (this.mode === 'single' && Number(this.quantity.value) < 1) throw new Error('Choose at least one cap.');
      const order = this.getOrder();
      const productQuantity = order.quantity + Number(this.dataset.existingProductQuantity || 0);
      for (const input of this.inputs) {
        const variant = this.rules.find(item => String(item.id) === input.dataset.variantId);
        this.querySelector(`[data-row-price="${variant.id}"]`).textContent = this.money(unitPrice(variant, variant.scope === 'product' ? productQuantity : Math.max(1, Number(input.value) || 1)));
      }
      this.querySelector('[data-total-quantity]').textContent = order.quantity;
      this.querySelector('[data-total-price]').textContent = this.money(order.subtotal);
      this.querySelector('[data-unit-price]').textContent = this.money(unitPrice(rule, rule.scope === 'product' ? productQuantity : Number(this.quantity.value) || 1));
      this.querySelector('[data-single-total]').textContent = this.mode === 'single' ? this.money(order.subtotal) : '—';
      this.status.textContent = '';
      this.querySelector('[data-add-all]').disabled = order.quantity === 0;
    } catch (error) {
      this.status.textContent = error.message;
      this.querySelector('[data-total-quantity]').textContent = '—';
      this.querySelector('[data-total-price]').textContent = '—';
      this.querySelector('[data-single-total]').textContent = '—';
      this.querySelector('[data-unit-price]').textContent = '—';
      this.querySelector(this.mode === 'single' ? '[data-add-single]' : '[data-add-all]').disabled = true;
    }
  }
  async submit(event, testOne = false) {
    event.preventDefault();
    if (this.busy || this.uncertain) return;
    let order;
    try { order = this.getOrder(testOne); } catch (error) { this.status.textContent = error.message; return; }
    if (!order.quantity) return;
    this.busy = true;
    this.buttons.forEach(button => { button.disabled = true; });
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = 'Adding your caps…';
    const controls = [...this.querySelectorAll('input, select, [data-mode], [data-color-choice]')];
    const previousDisabled = controls.map(input => input.disabled);
    controls.forEach(input => { input.disabled = true; });
    try {
      await submitOrder(this.dataset.addUrl, order.items);
      this.status.textContent = `${order.quantity} caps added. Opening your cart…`;
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      this.uncertain = true;
      this.status.textContent = error.name === 'TimeoutError' || error instanceof TypeError
        ? 'We could not confirm whether the caps were added. Review your cart before adding again.' : error.message;
      this.reviewLink.hidden = false;
      this.reviewLink.focus();
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      controls.forEach((input, index) => { input.disabled = previousDisabled[index]; });
    }
  }
}
if (!customElements.get('quick-order')) customElements.define('quick-order', QuickOrder);
